import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Globe, Sparkles, ArrowRight, Crown } from 'lucide-react';
import { useMarket } from '../hooks/useMarket';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Per-tab dedupe of which (county, area) we've already pinged this session, so
// scrolling back and forth doesn't inflate the impressions count.
const SESSION_KEY = 'lh_carousel_impr';
const getSessionId = () => {
  let sid = sessionStorage.getItem('lh_session_id');
  if (!sid) {
    sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('lh_session_id', sid);
  }
  return sid;
};
const recordImpression = (sponsor) => {
  try {
    const key = `${sponsor.county_slug}/${sponsor.area_slug || ''}`;
    const seen = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]');
    if (seen.includes(key)) return;
    seen.push(key);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(seen));
    axios
      .post(`${BACKEND_URL}/api/sponsors/impression`, {
        county_slug: sponsor.county_slug,
        area_slug: sponsor.area_slug || '',
        sponsor_id: sponsor.sponsor_id || '',
        session_id: getSessionId(),
      })
      .catch(() => {});
  } catch {
    /* sessionStorage unavailable — silently skip beacon */
  }
};

const titleCase = (s) =>
  (s || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');

const initials = (name) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/**
 * FeaturedAgentsCarousel — pulls every active area sponsor and displays them
 * in a horizontal carousel on the homepage. If zero sponsors are active we
 * render a single "Become a featured agent" promo card so the section is
 * always useful.
 */
const FeaturedAgentsCarousel = () => {
  const market = useMarket();
  const [sponsors, setSponsors] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/sponsors/featured?limit=12&country=${encodeURIComponent(market.name || 'Ireland')}`)
      .then((r) => !cancelled && setSponsors(r.data?.sponsors || []))
      .catch(() => !cancelled && setSponsors([]));
    return () => {
      cancelled = true;
    };
  }, [market.code, market.name]);

  if (sponsors === null) return null; // loading — silent

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100"
      data-testid="featured-agents-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-xs font-medium text-[#7042cc] tracking-widest uppercase mb-2">
              Featured estate agents
            </div>
            <h2
              className="text-3xl sm:text-4xl text-gray-900 tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
            >
              Local experts, area by area
            </h2>
            <p className="text-gray-600 text-base mt-3 max-w-2xl">
              Each agent is the exclusive sponsor of their patch — first-come, first-served. They know the streets, the schools, and the asking prices.
            </p>
          </div>
          <Link
            to={market.withPrefix('/advertise/area-sponsor')}
            data-testid="featured-agents-advertise-cta"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#7042cc] hover:text-[#5b35a8] group"
          >
            Become a featured agent
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <Carousel
          opts={{ align: 'start', loop: false, dragFree: true }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {sponsors.length === 0 ? (
              <CarouselItem
                key="cta"
                className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
              >
                <PromoCard />
              </CarouselItem>
            ) : (
              sponsors.map((s, i) => (
                <CarouselItem
                  key={`${s.county_slug}-${s.area_slug}-${i}`}
                  className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <AgentCard sponsor={s} />
                </CarouselItem>
              ))
            )}
            {sponsors.length > 0 && (
              <CarouselItem
                key="cta-tail"
                className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
              >
                <PromoCard />
              </CarouselItem>
            )}
          </CarouselContent>
          <CarouselPrevious
            className="hidden md:flex -left-3 bg-white border-[#7042cc]/20 text-[#7042cc] hover:bg-[#F5EFFF] hover:text-[#5b35a8]"
            data-testid="featured-agents-prev"
          />
          <CarouselNext
            className="hidden md:flex -right-3 bg-white border-[#7042cc]/20 text-[#7042cc] hover:bg-[#F5EFFF] hover:text-[#5b35a8]"
            data-testid="featured-agents-next"
          />
        </Carousel>
      </div>
    </section>
  );
};

const AgentCard = ({ sponsor }) => {
  const navigate = useNavigate();
  const place = sponsor.area_slug
    ? `${titleCase(sponsor.area_slug)}, ${titleCase(sponsor.county_slug)}`
    : titleCase(sponsor.county_slug);
  const areaHref = sponsor.area_slug
    ? `/buy/${sponsor.county_slug}/${sponsor.area_slug}`
    : `/buy/${sponsor.county_slug}`;
  const isPlus = sponsor.tier === 'plus';

  // Fire an impression beacon the first time this card is at least 50 % visible
  // in the viewport. De-duped per session per (county, area) so scrolling back
  // and forth doesn't inflate the count.
  const cardRef = React.useRef(null);
  React.useEffect(() => {
    if (!cardRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    const el = cardRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            recordImpression(sponsor);
            obs.disconnect();
          }
        });
      },
      { threshold: [0.5] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [sponsor]);

  return (
    // The whole card navigates to the area page on click; we use a div +
    // onClick rather than wrapping in <Link> because the card already
    // contains <a tel:> and <a href> for phone/website. Nested anchors
    // are invalid HTML and trigger React hydration warnings.
    <div
      ref={cardRef}
      role="link"
      tabIndex={0}
      onClick={() => navigate(areaHref)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(areaHref);
        }
      }}
      className="group block h-full cursor-pointer"
      data-testid={`featured-agent-card-${sponsor.county_slug}-${sponsor.area_slug || 'county'}`}
      data-tier={sponsor.tier || 'standard'}
    >
      <div
        className={`relative h-full bg-white rounded-2xl border hover:shadow-lg transition-all p-6 flex flex-col ${
          isPlus
            ? 'border-amber-300/60 ring-2 ring-amber-300/40 hover:ring-amber-400/60 hover:border-amber-400/70 shadow-amber-100/40 shadow-md'
            : 'border-gray-100 hover:border-[#7042cc]/30'
        }`}
      >
        {isPlus && (
          <div
            className="absolute -top-2.5 -right-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 text-white text-[10px] font-bold tracking-wider uppercase shadow-md"
            data-testid="featured-agent-plus-badge"
          >
            <Crown className="h-3 w-3" />
            Plus
          </div>
        )}
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0">
            {sponsor.agent_logo_url ? (
              <img
                src={sponsor.agent_logo_url}
                alt={sponsor.agent_name}
                className="h-14 w-14 rounded-xl object-cover border border-gray-100 bg-white"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div
                className={`h-14 w-14 rounded-xl text-white flex items-center justify-center font-semibold text-lg shadow-sm ${
                  isPlus
                    ? 'bg-gradient-to-br from-amber-400 to-amber-600'
                    : 'bg-gradient-to-br from-[#7042cc] to-[#5b35a8]'
                }`}
              >
                {initials(sponsor.agent_name)}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 truncate">
              {sponsor.agent_name}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
              <MapPin className={`h-3 w-3 ${isPlus ? 'text-amber-500' : 'text-[#7042cc]'}`} />
              <span className="truncate">{place}</span>
            </div>
          </div>
          {!isPlus && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F5EFFF] text-[#5b35a8] text-[10px] font-semibold tracking-wider uppercase">
              <Sparkles className="h-3 w-3" />
              Sponsor
            </span>
          )}
        </div>

        <h3 className="text-base font-semibold text-gray-900 mb-2 leading-snug line-clamp-2">
          {sponsor.headline}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4 flex-1">
          {sponsor.blurb}
        </p>

        <div className="flex items-center gap-3 pt-4 border-t border-gray-100 text-xs text-gray-500">
          {sponsor.agent_phone && (
            <a
              href={`tel:${sponsor.agent_phone}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 hover:text-[#7042cc]"
              data-testid={`featured-agent-phone-${sponsor.county_slug}`}
            >
              <Phone className="h-3.5 w-3.5" />
              <span>Call</span>
            </a>
          )}
          {sponsor.agent_website && (
            <a
              href={sponsor.agent_website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 hover:text-[#7042cc]"
              data-testid={`featured-agent-website-${sponsor.county_slug}`}
            >
              <Globe className="h-3.5 w-3.5" />
              <span>Website</span>
            </a>
          )}
          <span className="ml-auto text-[#7042cc] font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
            View area
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </div>
  );
};

const PromoCard = () => {
  const market = useMarket();
  const price = market.formatCurrency(99);
  return (
    <Link
      to={market.withPrefix('/advertise/area-sponsor')}
      className="group block h-full"
      data-testid="featured-agents-promo-card"
    >
      <div className="h-full bg-gradient-to-br from-[#F5EFFF] via-[#FFF7F0] to-[#FFEFE3] rounded-2xl border border-[#7042cc]/15 hover:border-[#7042cc]/40 hover:shadow-lg transition-all p-6 flex flex-col">
        <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#7042cc] text-white shadow-sm mb-4">
          <Sparkles className="h-5 w-5" />
        </div>
        <h3
          className="text-xl text-gray-900 mb-2 leading-snug"
          style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
        >
          Own your patch.
        </h3>
        <p className="text-sm text-gray-700 leading-relaxed mb-4 flex-1">
          Be the only agent featured on any {market.isUK ? 'region or postcode' : market.isUS ? 'metro or ZIP' : 'county or area'} landing page. {price} / 30 days, exclusive, first-come-first-served. Stripe checkout — no sales calls.
        </p>
        <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#7042cc] group-hover:gap-1.5 transition-all">
          Browse available areas
          <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
};

export default FeaturedAgentsCarousel;
